/**
 * The one and only DevFill gate.
 *
 * DevFill (the 🧪 "Fill test data" buttons + their faker-backed fillers) is on
 * when EITHER of these is true:
 *
 *   - `import.meta.env.DEV`  — a local `npm run dev` server, as before; or
 *   - `VITE_DEVFILL_ENABLED === 'true'` — a *build-time* opt-in so a deployed
 *     dev/staging build can carry DevFill too.
 *
 * Only the literal string `'true'` enables it. Anything else (unset, `'1'`,
 * `'TRUE'`, `'false'`) leaves it off — see `gate.test.ts`.
 *
 * ## Why this is a `const` and not (only) a function
 *
 * Prod safety here is *bundle exclusion*, not a runtime check: `vite build`
 * statically replaces `import.meta.env.DEV` with `false` and
 * `import.meta.env.VITE_DEVFILL_ENABLED` with the literal value from the build
 * env, so with the var unset this initialiser collapses to
 * `false || undefined === 'true'` → `false`. Rollup/esbuild then constant-folds
 * every `DEVFILL_ENABLED && <DevFillButton …/>` call site to dead code and
 * tree-shakes the buttons, the fillers and `@faker-js/faker` out of `dist`
 * entirely — exactly as the plain `import.meta.env.DEV &&` guards did before.
 *
 * A *function* call could not be folded away, so the branch (and faker) would
 * survive into prod. Hence: call sites must use the `DEVFILL_ENABLED` const.
 * `isDevFillEnabled()` exists only for non-DCE-critical/readable runtime use and
 * is a thin accessor over the same const — the expression lives here, once.
 *
 * Verified by `npm run check:dist-devfill` (see `scripts/check-dist-devfill.sh`,
 * whose header also covers enabling DevFill on a deployed environment).
 */
export const DEVFILL_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_DEVFILL_ENABLED === 'true';

/** Runtime accessor for the same gate. Prefer `DEVFILL_ENABLED` at JSX guards. */
export function isDevFillEnabled(): boolean {
  return DEVFILL_ENABLED;
}
